import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import { useMeasure } from 'react-use';
import React, { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { smallDesktop, desktopDevices, mobileDevices, instagramPosts } from '@/config/previewResolutions';
import generalConfig from '@/config/general';
import responsiveUtils from '@/utils/responsive';
import { usePreviewEditingListeners } from '@/components/Editor/hooks/useEditingAreas';
import { deselectEditingArea, getContextEditingSectionAttr, getEditingSectionAttr, getEditingWidgetToolbarAttr } from '@/components/Editor/hooks/useEditingAreas';
import WidgetPreviewToolbar from './Toolbar';
import ResizableContainer from './ResizableContainer';
import LayoutControls from './LayoutControls';
import useContainerResize from './ResizableContainer/useContainerResize';
import usePreviewResolution from './usePreviewResolution';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PREVIEW_SCALE_FACTOR = 1.2; // 120% of original size

const WidgetPreviewContainer = _ref => {
  let {
    className,
    mainClassName = '',
    widget,
    editing = false,
    hasErrors = false,
    originFileType = '',
    isDataButtonHidden,
    isResolutionControlHidden,
    isResolutionGroupControlHidden,
    isPreviewContainerCollapsed,
    isCustomResolutionControlHidden,
    isZoomControlHidden,
    isToolbarVisible,
    isAiExperiment,
    isScrollable = true,
    initialResolutionStrategy,
    zoomLevel: controlledZoomLevel,
    defaultZoomLevel = 100,
    defaultAspectRatio = instagramPosts.value,
    defaultPreviewResolution = smallDesktop,
    updatedAt = 1,
    containerMinSize,
    previewGroups = [mobileDevices, desktopDevices],
    textOverrides = {},
    onWidgetUpdate,
    onPreviewResolutionChange = () => undefined,
    onEditData,
    onEditDataErrors,
    onSave,
    onShare,
    onEditTheme,
    children
  } = _ref;
  const scrollContainerEl = useRef(null);
  const previewContainerEl = useRef(null);
  const toolbarEl = useRef(null);
  const {
    previewResolution,
    activePreviewGroup,
    updateActivePreviewGroup,
    updatePreviewResolution,
    hasDiscoveredResolution,
    zoomLevel,
    adjustZoomLevel,
    aspectRatio,
    adjustAspectRatio
  } = usePreviewResolution({
    previewGroups,
    defaultZoomLevel,
    defaultAspectRatio,
    initialResolutionStrategy,
    defaultPreviewResolution,
    containerRef: scrollContainerEl,
    containerMinSize,
    onPreviewResolutionChange
  });
  const zoomLevelValue = controlledZoomLevel ?? zoomLevel;
  const hasChanges = updatedAt > 1;
  const {
    0: hasFocus,
    1: setHasFocus
  } = useState(false);
  const {
    dimensions,
    tooltipPosRef,
    containerRef,
    resizingRef,
    handleMouseDown,
    handleTouchStart
  } = useContainerResize({
    enableResize: editing && hasFocus,
    zoomLevel: zoomLevelValue,
    updatePreviewResolution,
    onFocusedChange: setHasFocus
  }); // Responsive queries

  const [containerMeasureRef, {
    width: containerWidth
  }] = useMeasure();
  const isSmallContainer = useMemo(() => responsiveUtils.isSmall(containerWidth), [containerWidth]); // Add edit area event listeners
  // to preview container

  usePreviewEditingListeners({
    previewEl: editing && scrollContainerEl.current || undefined
  }); // Only deselect when user clicks
  // outside a widget editing boundary
  // or when there is no context editor

  const handleDeselectEditingArea = useCallback(evt => {
    var _evt$target;

    const targetEl = evt.target;
    if (targetEl && targetEl.closest(`[${getEditingSectionAttr()}]`)) return;
    if (document.querySelector(`[${getContextEditingSectionAttr()}]`)) return;
    if (targetEl && targetEl.closest(`[${getEditingWidgetToolbarAttr()}]`)) return; // ignore clicks inside the nested floating UI portal

    if (evt !== null && evt !== void 0 && (_evt$target = evt.target) !== null && _evt$target !== void 0 && _evt$target.closest('[data-floating-ui-portal]')) {
      return;
    }

    deselectEditingArea();
  }, []); // Calculate widget preview size

  const widgetPreviewSize = useMemo(() => {
    return {
      originalWidth: previewResolution.value[0],
      originalHeight: previewResolution.value[1],
      width: previewResolution.value[0] * PREVIEW_SCALE_FACTOR,
      height: previewResolution.value[1] * PREVIEW_SCALE_FACTOR
    };
  }, [previewResolution]); // Set initial scroll position to center the box in the viewport

  useEffect(() => {
    var _toolbarEl$current;

    if (!scrollContainerEl.current || !previewContainerEl.current || !isScrollable) {
      return;
    }

    const scrollContainerSizes = scrollContainerEl.current.getBoundingClientRect();
    const previewContainerSizes = previewContainerEl.current.getBoundingClientRect();
    const toolbarSizes = ((_toolbarEl$current = toolbarEl.current) === null || _toolbarEl$current === void 0 ? void 0 : _toolbarEl$current.getBoundingClientRect()) || {
      height: 0
    }; // Calculate the scroll position to center the preview

    const scrollLeft = (previewContainerSizes.width - scrollContainerSizes.width) / 2;
    const scrollTop = (previewContainerSizes.height - toolbarSizes.height - scrollContainerSizes.height) / 2; // Set the scroll position

    scrollContainerEl.current.scrollLeft = scrollLeft;
    scrollContainerEl.current.scrollTop = scrollTop;
  }, [widgetPreviewSize.width, widgetPreviewSize.height, isScrollable]);
  return /*#__PURE__*/_jsxs("div", {
    className: clsx(styles.container, className || ''),
    ref: containerMeasureRef,
    children: [/*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
      ref: scrollContainerEl,
      className: clsx(styles.scrollBoundary, {
        [styles[`scrollBoundary--disabled`]]: !isScrollable
      })
    }, editing && {
      onClick: handleDeselectEditingArea
    }), {}, {
      children: /*#__PURE__*/_jsxs("div", {
        ref: previewContainerEl,
        className: clsx(styles.preview, mainClassName, isPreviewContainerCollapsed ? styles['preview--collapsed'] : ''),
        style: {
          [`--${generalConfig.productNameSpace}-widget-preview-width`]: `${widgetPreviewSize.width}px`,
          [`--${generalConfig.productNameSpace}-widget-preview-height`]: `${widgetPreviewSize.height}px`
        },
        children: [/*#__PURE__*/_jsx(ResizableContainer, {
          dimensions: dimensions,
          containerRef: containerRef,
          resizingRef: resizingRef,
          tooltipPosRef: tooltipPosRef,
          enableResize: editing && hasFocus,
          onFocusedChange: setHasFocus,
          handleTouchStart: handleTouchStart,
          handleMouseDown: handleMouseDown,
          style: {
            transform: zoomLevelValue !== 100 ? `scale(${zoomLevelValue / 100})` : undefined
          },
          children: /*#__PURE__*/_jsx("div", {
            className: clsx(styles.preview__canvas, {
              [styles['preview__canvas--focus']]: hasFocus,
              [styles['preview__canvas--hasBorders']]: isScrollable
            }),
            style: {
              width: `${(dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) ?? previewResolution.value[0]}px`,
              height: isPreviewContainerCollapsed ? 'inherit' : `${(dimensions === null || dimensions === void 0 ? void 0 : dimensions.height) ?? previewResolution.value[1]}px`,
              maxHeight: isPreviewContainerCollapsed ? `${(dimensions === null || dimensions === void 0 ? void 0 : dimensions.height) ?? previewResolution.value[1]}px` : 'inherit'
            },
            children: hasDiscoveredResolution && children
          })
        }), isToolbarVisible ? /*#__PURE__*/_jsx(WidgetPreviewToolbar, {
          ref: toolbarEl,
          isSmallContainer: isSmallContainer,
          isAiExperiment: isAiExperiment,
          widget: widget,
          previewGroups: previewGroups,
          previewResolution: previewResolution,
          activePreviewGroup: activePreviewGroup,
          aspectRatio: aspectRatio,
          defaultAspectRatio: defaultAspectRatio,
          textOverrides: {
            canvasLabel: textOverrides === null || textOverrides === void 0 ? void 0 : textOverrides.toolbarCanvasLabel
          },
          adjustAspectRatio: adjustAspectRatio,
          updatePreviewResolution: updatePreviewResolution,
          updateActivePreviewGroup: updateActivePreviewGroup,
          hasChanges: hasChanges,
          onEditData: onEditData,
          onEditTheme: onEditTheme,
          onSave: onSave,
          onShare: onShare,
          onWidgetUpdate: onWidgetUpdate
        }) : null]
      })
    })), /*#__PURE__*/_jsx(LayoutControls, {
      zoomLevel: zoomLevel,
      hasErrors: hasErrors,
      originFileType: originFileType,
      previewGroups: previewGroups,
      previewResolution: previewResolution,
      updatePreviewResolution: updatePreviewResolution,
      activePreviewGroup: activePreviewGroup,
      updateActivePreviewGroup: updateActivePreviewGroup,
      adjustZoomLevel: adjustZoomLevel,
      isZoomControlHidden: isZoomControlHidden,
      isResolutionControlHidden: isResolutionControlHidden,
      isDataButtonHidden: isDataButtonHidden,
      isCustomResolutionControlHidden: isCustomResolutionControlHidden,
      isResolutionGroupControlHidden: isResolutionGroupControlHidden,
      onEditData: onEditData,
      onEditDataErrors: onEditDataErrors
    })]
  });
};

export default WidgetPreviewContainer;